// client/src/pages/Landing.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ProModal from '../components/ProModal';
import GuestPromptModal from '../components/GuestPromptModal';
import SearchAutocomplete from '../components/SearchAutocomplete';

const STATUS_OPTIONS = ['RECRUITING','NOT_YET_RECRUITING','COMPLETED','ACTIVE_NOT_RECRUITING','TERMINATED','WITHDRAWN','SUSPENDED','ENROLLING_BY_INVITATION'];
const PHASE_OPTIONS = ['PHASE1','PHASE2','PHASE3','PHASE4','EARLY_PHASE1','NA'];
const AGE_OPTIONS = ['CHILD','ADULT','OLDER_ADULT'];
const STUDY_TYPE_OPTIONS = ['INTERVENTIONAL','OBSERVATIONAL','EXPANDED_ACCESS'];
const FUNDER_OPTIONS = ['NIH','OTHER_US_FED','INDUSTRY','OTHER'];
const SEX_OPTIONS = ['FEMALE','MALE','ALL'];

const STATUS_COLORS = {
  RECRUITING: { bg: '#dcfce7', color: '#15803d' },
  NOT_YET_RECRUITING: { bg: '#dbeafe', color: '#1d4ed8' },
  COMPLETED: { bg: '#f3f4f6', color: '#374151' },
  ACTIVE_NOT_RECRUITING: { bg: '#fef9c3', color: '#854d0e' },
  TERMINATED: { bg: '#fee2e2', color: '#b91c1c' },
  WITHDRAWN: { bg: '#fce7f3', color: '#be185d' },
  SUSPENDED: { bg: '#ede9fe', color: '#7c3aed' },
  ENROLLING_BY_INVITATION: { bg: '#e0f2fe', color: '#0369a1' },
  default: { bg: '#f3f4f6', color: '#374151' }
};

function StatusTag({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.default;
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, padding: '2px 7px', borderRadius: 0, background: c.bg, color: c.color, letterSpacing: '0.3px' }}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
}

const chip = (active) => ({
  display: 'inline-flex', alignItems: 'center',
  padding: '3px 9px', border: `1px solid ${active ? 'var(--ink)' : 'var(--line-card)'}`,
  borderRadius: 0, fontSize: 11, margin: '2px', cursor: 'pointer',
  userSelect: 'none', fontFamily: 'var(--font-mono)',
  background: active ? 'var(--ink)' : 'var(--card)', color: active ? 'var(--paper)' : 'var(--muted)',
  transition: 'all 0.1s'
});

const inp = {
  width: '100%', padding: '8px 10px', border: '1px solid var(--line)',
  borderRadius: 0, fontSize: 13, outline: 'none', fontFamily: 'var(--font-body)',
  boxSizing: 'border-box'
};

const lbl = {
  fontSize: 10, fontWeight: 700, letterSpacing: '1.5px', color: 'var(--muted-2)',
  textTransform: 'uppercase', marginBottom: 8, display: 'block',
  fontFamily: 'var(--font-mono)'
};

export default function Landing() {
  const { user, isPro } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPro, setShowPro] = useState(false);
  const [showGuest, setShowGuest] = useState(false);
  const [studies, setStudies] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pageTokens, setPageTokens] = useState(['']);
  const [currentPage, setCurrentPage] = useState(1);
  const [statsData, setStatsData] = useState(null);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [savePresetModal, setSavePresetModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetSaving, setPresetSaving] = useState(false);

  // Filters
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [statuses, setStatuses] = useState([]);
  const [phases, setPhases] = useState([]);
  const [ages, setAges] = useState([]);
  const [studyTypes, setStudyTypes] = useState([]);
  const [funders, setFunders] = useState([]);
  const [sex, setSex] = useState('');
  const [hasResults, setHasResults] = useState('');
  const [healthyVolunteers, setHealthyVolunteers] = useState('');
  const [sponsor, setSponsor] = useState('');
  const [intervention, setIntervention] = useState('');
  const [location, setLocation] = useState('');
  const [startDateFrom, setStartDateFrom] = useState('');
  const [startDateTo, setStartDateTo] = useState('');
  const [completionDateFrom, setCompletionDateFrom] = useState('');
  const [completionDateTo, setCompletionDateTo] = useState('');
  const [firstPostedFrom, setFirstPostedFrom] = useState('');
  const [sort, setSort] = useState('');
  const [advancedQuery, setAdvancedQuery] = useState('');

  const abortRef = useRef(null);

  const guestGate = (fn) => (...args) => {
    if (!user) { setShowGuest(true); return; }
    return fn(...args);
  };

  useEffect(() => {
    if (user) {
      axios.get('/api/trials/stats/size').then(({ data }) => setStatsData(data)).catch(() => {});
    }
  }, [user]);

  const buildAdvanced = useCallback(() => {
    const parts = [];
    if (phases.length) parts.push(`AREA[Phase](${phases.map(ph => `"${ph}"`).join(' OR ')})`);
    if (studyTypes.length) parts.push(`AREA[StudyType](${studyTypes.map(t => `"${t}"`).join(' OR ')})`);
    if (funders.length) parts.push(`AREA[LeadSponsorClass](${funders.map(f => `"${f}"`).join(' OR ')})`);
    if (sex && sex !== 'ALL') parts.push(`AREA[Sex]"${sex}"`);
    if (healthyVolunteers === 'yes') parts.push(`AREA[HealthyVolunteers]"Yes"`);

    // Date ranges
    if (startDateFrom && startDateTo) parts.push(`AREA[StartDate]RANGE[${startDateFrom},${startDateTo}]`);
    else if (startDateFrom) parts.push(`AREA[StartDate]RANGE[${startDateFrom},MAX]`);
    else if (startDateTo) parts.push(`AREA[StartDate]RANGE[MIN,${startDateTo}]`);

    if (completionDateFrom && completionDateTo) parts.push(`AREA[CompletionDate]RANGE[${completionDateFrom},${completionDateTo}]`);
    else if (completionDateFrom) parts.push(`AREA[CompletionDate]RANGE[${completionDateFrom},MAX]`);
    else if (completionDateTo) parts.push(`AREA[CompletionDate]RANGE[MIN,${completionDateTo}]`);

    if (firstPostedFrom) parts.push(`AREA[StudyFirstPostDate]RANGE[${firstPostedFrom},MAX]`);
    if (advancedQuery) parts.push(advancedQuery);
    return parts.join(' AND ') || undefined;
  }, [phases, studyTypes, funders, sex, healthyVolunteers, startDateFrom, startDateTo, completionDateFrom, completionDateTo, firstPostedFrom, advancedQuery]);

  const buildParams = useCallback((token = '') => {
    const p = { pageSize: 15, countTotal: true };
    if (token) p.pageToken = token;
    if (query) p['query.cond'] = query;
    if (sponsor) p['query.spons'] = sponsor;
    if (intervention) p['query.intr'] = intervention;
    if (location) p['query.locn'] = location;
    if (statuses.length) p['filter.overallStatus'] = statuses.join(',');
    const adv = buildAdvanced();
    if (adv) p['filter.advanced'] = adv;
    if (sort) p.sort = sort;
    p.fields = 'NCTId,BriefTitle,OverallStatus,Phase,Condition,BriefSummary,LeadSponsorName,HasResults,StudyType,EnrollmentCount';
    return p;
  }, [query, sponsor, intervention, location, statuses, buildAdvanced, sort]);

  const fetchStudies = useCallback(async (token = '', resetPage = true) => {
    if (!user) return;
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setLoading(true);
    setError('');
    try {
      const params = buildParams(token);
      const { data } = await axios.get('/api/trials/studies', { params, signal: abortRef.current.signal });
      setStudies(data.studies || []);
      setTotalCount(data.totalCount || 0);
      if (resetPage) {
        setCurrentPage(1);
        setPageTokens(['', data.nextPageToken || null]);
      } else {
        setPageTokens(prev => {
          const updated = [...prev];
          if (data.nextPageToken) updated.push(data.nextPageToken);
          return updated;
        });
      }
    } catch (err) {
      if (err.name !== 'CanceledError') setError('Failed to fetch studies. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [buildParams, user]);

  useEffect(() => { if (user) fetchStudies('', true); }, [user]); // eslint-disable-line
  useEffect(() => { if (user) fetchStudies('', true); }, [query, statuses, sort]); // eslint-disable-line

  const handleSearch = (term) => {
    if (!user) { setShowGuest(true); return; }
    setQuery(term);
    if (term) setSearchParams({ q: term });
    else setSearchParams({});
  };

  const toggleArr = guestGate((arr, setArr, val) => {
    setArr(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  });

  const handleApply = guestGate(() => fetchStudies('', true));

  const handlePageChange = async (page) => {
    const token = pageTokens[page - 1] || '';
    setCurrentPage(page);
    await fetchStudies(token, false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExport = async () => {
    if (!user) { setShowGuest(true); return; }
    if (!isPro()) { setShowPro(true); return; }
    try {
      const params = buildParams();
      delete params.fields;
      delete params.countTotal;
      const { data } = await axios.get('/api/trials/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `biomedarticles-export-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Export failed. Please try again.');
    }
  };

  const handleSaveSearch = async () => {
    if (!presetName.trim()) return;
    setPresetSaving(true);
    try {
      const params = buildParams();
      await axios.post('/api/trials/saved-searches', { name: presetName, params });
      setSavePresetModal(false);
      setPresetName('');
      alert('Search preset saved! View it in Profile → Searches.');
    } catch (err) {
      if (err.response?.data?.requiresPro) { setShowPro(true); setSavePresetModal(false); }
      else alert(err.response?.data?.message || 'Failed to save preset.');
    }
    setPresetSaving(false);
  };

  const clearFilters = () => {
    setStatuses([]); setPhases([]); setAges([]); setStudyTypes([]);
    setFunders([]); setSex(''); setHasResults(''); setHealthyVolunteers('');
    setSponsor(''); setIntervention(''); setLocation('');
    setStartDateFrom(''); setStartDateTo('');
    setCompletionDateFrom(''); setCompletionDateTo('');
    setFirstPostedFrom(''); setAdvancedQuery(''); setSort('');
  };

  const activeFilterCount = statuses.length + phases.length + ages.length + studyTypes.length +
    funders.length + (sex ? 1 : 0) + (hasResults ? 1 : 0) + (healthyVolunteers ? 1 : 0) +
    (sponsor ? 1 : 0) + (intervention ? 1 : 0) + (location ? 1 : 0) +
    (startDateFrom || startDateTo ? 1 : 0) + (completionDateFrom || completionDateTo ? 1 : 0) +
    (advancedQuery ? 1 : 0);

  const totalPages = Math.min(Math.ceil(totalCount / 15), 100);

  // ── Sidebar content (shared between desktop + mobile drawer) ──
  const SidebarContent = () => (
    <div style={{ padding: '20px 18px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase' }}>
          Filters{activeFilterCount > 0 && (
            <span style={{ background: 'var(--ink)', color: 'var(--paper)', borderRadius: 0, padding: '1px 6px', fontSize: 10, marginLeft: 6 }}>{activeFilterCount}</span>
          )}
        </span>
        {activeFilterCount > 0 && (
          <button onClick={clearFilters} style={{ background: 'none', border: 'none', color: 'var(--muted-2)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--font-mono)' }}>Clear all</button>
        )}
      </div>

      <div style={{ marginBottom: 18 }}>
        <span style={lbl}>Status</span>
        {STATUS_OPTIONS.map(st => <span key={st} style={chip(statuses.includes(st))} onClick={() => toggleArr(statuses, setStatuses, st)}>{st.replace(/_/g, ' ')}</span>)}
      </div>

      <div style={{ marginBottom: 18 }}>
        <span style={lbl}>Phase</span>
        {PHASE_OPTIONS.map(ph => <span key={ph} style={chip(phases.includes(ph))} onClick={() => toggleArr(phases, setPhases, ph)}>{ph.replace('PHASE', 'P').replace('EARLY_PHASE1', 'Early P1').replace('NA', 'N/A')}</span>)}
      </div>

      <div style={{ marginBottom: 18 }}>
        <span style={lbl}>Age Group</span>
        {AGE_OPTIONS.map(ag => <span key={ag} style={chip(ages.includes(ag))} onClick={() => toggleArr(ages, setAges, ag)}>{ag.replace('_', ' ')}</span>)}
      </div>

      <div style={{ marginBottom: 18 }}>
        <span style={lbl}>Study Type</span>
        {STUDY_TYPE_OPTIONS.map(t => <span key={t} style={chip(studyTypes.includes(t))} onClick={() => toggleArr(studyTypes, setStudyTypes, t)}>{t.replace('_', ' ')}</span>)}
      </div>

      <div style={{ marginBottom: 18 }}>
        <span style={lbl}>Sponsor</span>
        <input style={inp} placeholder="e.g. Pfizer, NIH..." value={sponsor}
          onChange={e => setSponsor(e.target.value)}
          onFocus={!user ? () => setShowGuest(true) : undefined}
          onKeyDown={e => e.key === 'Enter' && handleApply()} />
      </div>

      <div style={{ marginBottom: 18 }}>
        <span style={lbl}>Intervention / Treatment</span>
        <input style={inp} placeholder="e.g. drug name, therapy..." value={intervention}
          onChange={e => setIntervention(e.target.value)}
          onFocus={!user ? () => setShowGuest(true) : undefined}
          onKeyDown={e => e.key === 'Enter' && handleApply()} />
      </div>

      <div style={{ marginBottom: 18 }}>
        <span style={lbl}>Location</span>
        <input style={inp} placeholder="City, state, or country..." value={location}
          onChange={e => setLocation(e.target.value)}
          onFocus={!user ? () => setShowGuest(true) : undefined}
          onKeyDown={e => e.key === 'Enter' && handleApply()} />
      </div>

      {/* More Filters toggle */}
      <button
        onClick={() => setShowMoreFilters(m => !m)}
        style={{ width: '100%', background: 'none', border: '1px solid var(--line)', borderRadius: 0, padding: '8px', fontSize: 12, cursor: 'pointer', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: 'var(--font-body)', color: 'var(--muted)' }}
      >
        <span>More Filters</span>
        <span>{showMoreFilters ? '−' : '+'}</span>
      </button>

      {showMoreFilters && (
        <>
          <div style={{ marginBottom: 18 }}>
            <span style={lbl}>Study Start Date</span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--muted-2)', marginBottom: 4 }}>FROM</div>
                <input type="date" style={inp} value={startDateFrom} onChange={e => setStartDateFrom(e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--muted-2)', marginBottom: 4 }}>TO</div>
                <input type="date" style={inp} value={startDateTo} onChange={e => setStartDateTo(e.target.value)} />
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <span style={lbl}>Primary Completion Date</span>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--muted-2)', marginBottom: 4 }}>FROM</div>
                <input type="date" style={inp} value={completionDateFrom} onChange={e => setCompletionDateFrom(e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--muted-2)', marginBottom: 4 }}>TO</div>
                <input type="date" style={inp} value={completionDateTo} onChange={e => setCompletionDateTo(e.target.value)} />
              </div>
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <span style={lbl}>First Posted (from)</span>
            <input type="date" style={inp} value={firstPostedFrom} onChange={e => setFirstPostedFrom(e.target.value)} />
          </div>

          <div style={{ marginBottom: 18 }}>
            <span style={lbl}>Sex</span>
            {SEX_OPTIONS.map(s => (
              <span key={s} style={chip(sex === s)} onClick={guestGate(() => setSex(sex === s ? '' : s))}>{s}</span>
            ))}
          </div>

          <div style={{ marginBottom: 18 }}>
            <span style={lbl}>Funder Type</span>
            {FUNDER_OPTIONS.map(f => (
              <span key={f} style={chip(funders.includes(f))} onClick={() => toggleArr(funders, setFunders, f)}>
                {f === 'OTHER_US_FED' ? 'US Fed' : f === 'INDUSTRY' ? 'Industry' : f}
              </span>
            ))}
          </div>

          <div style={{ marginBottom: 18 }}>
            <span style={lbl}>Study Results</span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[['', 'All'], ['yes', 'With Results'], ['no', 'Without Results']].map(([val, label]) => (
                <span key={val} style={chip(hasResults === val)} onClick={guestGate(() => setHasResults(val))}>{label}</span>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <span style={lbl}>Healthy Volunteers</span>
            <div style={{ display: 'flex', gap: 6 }}>
              {[['', 'All'], ['yes', 'Accepted']].map(([val, label]) => (
                <span key={val} style={chip(healthyVolunteers === val)} onClick={guestGate(() => setHealthyVolunteers(val))}>{label}</span>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Advanced (Pro) */}
      <div style={{ marginTop: 4, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 10 }}>Advanced</div>
        {user && isPro() ? (
          <div>
            <span style={lbl}>Essie Expression</span>
            <textarea
              style={{ ...inp, resize: 'vertical', minHeight: 72, fontSize: 12, fontFamily: 'var(--font-mono)' }}
              placeholder="AREA[MinimumAge]RANGE[MIN, 16 years]"
              value={advancedQuery}
              onChange={e => setAdvancedQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && e.ctrlKey && fetchStudies('', true)}
            />
            <div style={{ fontSize: 10, color: 'var(--muted-2)', marginTop: 4 }}>Ctrl+Enter to apply</div>
          </div>
        ) : (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--muted)', padding: '8px 10px', background: 'var(--paper-2)', borderRadius: 0, cursor: 'pointer', borderLeft: '2px solid var(--ink)' }}
            onClick={() => user ? setShowPro(true) : setShowGuest(true)}
          >
            <span>🔒</span>
            <span>Essie expressions · <strong>{user ? 'Pro' : 'Register first'}</strong></span>
          </div>
        )}
      </div>

      <button
        style={{ width: '100%', background: 'var(--ink)', color: 'var(--paper)', border: 'none', padding: '11px', borderRadius: 0, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 14 }}
        onClick={handleApply}
      >
        Apply Filters
      </button>
    </div>
  );

  return (
    <div style={{ minHeight: 'calc(100vh - 56px)', background: 'var(--paper)' }}>
      {showPro && <ProModal onClose={() => setShowPro(false)} />}
      {showGuest && <GuestPromptModal onClose={() => setShowGuest(false)} />}

      {/* Save Preset Modal */}
      {savePresetModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={e => e.target === e.currentTarget && setSavePresetModal(false)}>
          <div style={{ background: 'var(--card)', borderRadius: 0, padding: '32px', maxWidth: 400, width: '100%', border: '1px solid var(--line-card)', boxShadow: '6px 6px 0 rgba(10,10,10,0.06)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 8, color: 'var(--ink)' }}>Save Search Preset</div>
            <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>Name this search so you can reload it instantly later.</p>
            <input
              style={{ ...inp, marginBottom: 16 }}
              placeholder="e.g. Recruiting Diabetes Phase 3 USA"
              value={presetName}
              onChange={e => setPresetName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSaveSearch()}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                style={{ flex: 1, background: 'var(--ink)', color: 'var(--paper)', border: 'none', padding: '11px', fontSize: 14, fontWeight: 600, borderRadius: 0, cursor: 'pointer', opacity: presetSaving ? 0.7 : 1 }}
                onClick={handleSaveSearch} disabled={presetSaving}
              >
                {presetSaving ? 'Saving...' : 'Save Preset'}
              </button>
              <button
                style={{ flex: 1, background: 'transparent', color: 'var(--ink)', border: '1.5px solid var(--ink)', padding: '11px', fontSize: 14, borderRadius: 0, cursor: 'pointer' }}
                onClick={() => setSavePresetModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <div style={{ background: 'var(--paper)', color: 'var(--ink)', borderBottom: '2px solid var(--ink)', padding: '52px 24px 44px', textAlign: 'center' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '3px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 18 }}>
          Clinical Research Platform
        </div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 5.5vw, 60px)', lineHeight: 1.05, marginBottom: 14, color: 'var(--ink)' }}>
          Search Every<br /><em>Clinical Trial</em>
        </h1>
        <p style={{ color: 'var(--muted)', fontSize: 15, maxWidth: 480, margin: '0 auto 36px', lineHeight: 1.6 }}>
          Search {statsData ? Number(statsData.totalStudies).toLocaleString() : '574,000+'} studies from ClinicalTrials.gov
        </p>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <SearchAutocomplete
            onSearch={handleSearch}
            onGuestClick={() => setShowGuest(true)}
            isGuest={!user}
            initialValue={query}
          />
        </div>
        <div style={{ display: 'flex', gap: 48, justifyContent: 'center', marginTop: 28, flexWrap: 'wrap' }}>
          {[
            { num: statsData ? Number(statsData.totalStudies).toLocaleString() : '574,390', label: 'Total Studies' },
            { num: '50+', label: 'Countries' },
            { num: 'Live', label: 'Data' }
          ].map(item => (
            <div key={item.label} style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 20, color: 'var(--ink)', fontWeight: 500 }}>{item.num}</div>
              <div style={{ fontSize: 10, color: 'var(--muted-2)', letterSpacing: '1.5px', textTransform: 'uppercase', marginTop: 3 }}>{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Guest banner */}
      {!user && (
        <div style={{ background: 'var(--paper-2)', borderBottom: '1px solid var(--line)', padding: '11px 24px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--muted)', fontSize: 13 }}>Register free to search and explore all clinical trials</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ background: 'transparent', color: 'var(--ink)', border: '1.5px solid var(--ink)', padding: '5px 14px', fontSize: 12, borderRadius: 0, cursor: 'pointer' }} onClick={() => navigate('/login')}>Sign in</button>
            <button style={{ background: 'var(--ink)', color: 'var(--paper)', border: 'none', padding: '5px 14px', fontSize: 12, fontWeight: 700, borderRadius: 0, cursor: 'pointer' }} onClick={() => navigate('/register')}>Get free access →</button>
          </div>
        </div>
      )}

      {/* Mobile filter button */}
      <div className="mobile-filter-btn" style={{ display: 'none' }}>
        <button
          onClick={() => setSidebarOpen(true)}
          style={{ margin: '12px 16px', padding: '10px 20px', background: 'var(--ink)', color: 'var(--paper)', border: 'none', borderRadius: 0, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          ⚙ Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
        </button>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex' }}>
          <div style={{ flex: 1, background: 'rgba(0,0,0,0.5)' }} onClick={() => setSidebarOpen(false)} />
          <div style={{ width: 300, background: 'var(--card)', overflowY: 'auto', boxShadow: '-4px 0 20px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line-card)', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700 }}>Filters</span>
              <button onClick={() => setSidebarOpen(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <SidebarContent />
          </div>
        </div>
      )}

      {/* Main layout */}
      <div style={{ display: 'flex', maxWidth: 1440, margin: '0 auto' }}>
        {/* Desktop sidebar */}
        <aside className="desktop-sidebar" style={{ width: 272, flexShrink: 0, borderRight: '1px solid var(--line)', background: 'var(--card)', position: 'sticky', top: 56, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 56px)', overflowY: 'auto' }}>
          <SidebarContent />
        </aside>

        {/* Results */}
        <main className="results-main" style={{ flex: 1, padding: '24px 28px', minWidth: 0 }}>
          {/* Results header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--muted)' }}>
              {!user ? 'Register to see results' : loading ? 'Searching...' : totalCount ? `${Number(totalCount).toLocaleString()} studies found` : 'No results'}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 12, background: 'var(--card)', cursor: 'pointer', fontFamily: 'var(--font-mono)' }}
                onClick={handleExport}
              >
                {!user ? '🔒 Export' : isPro() ? '↓ Export CSV' : '🔒 Export (Pro)'}
              </button>
              {user && isPro() && (
                <button
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', border: '1px solid var(--line)', borderRadius: 0, fontSize: 12, background: 'var(--card)', cursor: 'pointer', fontFamily: 'var(--font-mono)' }}
                  onClick={() => setSavePresetModal(true)}
                >
                  🔖 Save Search
                </button>
              )}
              {[{ label: 'Relevance', val: '' }, { label: 'Newest', val: 'LastUpdatePostDate:desc' }, { label: 'Enrolled', val: 'EnrollmentCount:desc' }].map(opt => (
                <button
                  key={opt.val}
                  style={{ padding: '5px 12px', border: `1px solid ${sort === opt.val ? 'var(--ink)' : 'var(--line)'}`, borderRadius: 0, fontSize: 12, background: sort === opt.val ? 'var(--ink)' : 'var(--card)', color: sort === opt.val ? 'var(--paper)' : 'var(--ink)', cursor: 'pointer', fontFamily: 'var(--font-mono)', transition: 'all 0.1s' }}
                  onClick={guestGate(() => setSort(opt.val))}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Guest blurred preview */}
          {!user ? (
            <div style={{ position: 'relative' }}>
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '18px 22px', marginBottom: 10, filter: 'blur(3px)', pointerEvents: 'none', opacity: 1 - i * 0.12 }}>
                  <div style={{ background: 'var(--line-card)', height: 20, borderRadius: 0, width: `${65 + i * 6}%`, marginBottom: 10 }} />
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    {[60, 90, 110].map(w => <div key={w} style={{ background: 'var(--line-card)', height: 18, width: w, borderRadius: 0 }} />)}
                  </div>
                  <div style={{ background: 'var(--paper-2)', height: 14, borderRadius: 0, width: '90%', marginBottom: 6 }} />
                  <div style={{ background: 'var(--paper-2)', height: 14, borderRadius: 0, width: '70%' }} />
                </div>
              ))}
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 0%, rgba(244,241,234,0.98) 55%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', paddingBottom: 48 }}>
                <div style={{ textAlign: 'center', maxWidth: 360 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 8, color: 'var(--ink)' }}>Access 574,000+ Studies</div>
                  <p style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>Create a free account to search and explore the full clinical trials database.</p>
                  <button style={{ display: 'block', width: '100%', background: 'var(--ink)', color: 'var(--paper)', border: 'none', padding: '13px', fontSize: 15, fontWeight: 700, borderRadius: 0, cursor: 'pointer', marginBottom: 10 }} onClick={() => navigate('/register')}>Create free account →</button>
                  <button style={{ display: 'block', width: '100%', background: 'transparent', color: 'var(--ink)', border: '1.5px solid var(--ink)', padding: '12px', fontSize: 14, fontWeight: 500, borderRadius: 0, cursor: 'pointer' }} onClick={() => navigate('/login')}>Sign in</button>
                </div>
              </div>
            </div>
          ) : loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', gap: 14 }}>
              <div className="spin" style={{ width: 28, height: 28, border: '2px solid var(--line-card)', borderTopColor: 'var(--ink)', borderRadius: '50%' }} />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--muted)' }}>Searching clinical database...</span>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '60px 24px', color: 'var(--danger)' }}>{error}</div>
          ) : studies.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--muted)' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--ink)', marginBottom: 8 }}>No studies found</div>
              <p>Try adjusting your search terms or filters</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gap: 10 }}>
                {studies.map(study => {
                  const p = study.protocolSection;
                  const id = p?.identificationModule;
                  const st = p?.statusModule;
                  const design = p?.designModule;
                  const desc = p?.descriptionModule;
                  const sp = p?.sponsorCollaboratorsModule?.leadSponsor;
                  const conditions = p?.conditionsModule?.conditions || [];
                  return (
                    <div
                      key={id?.nctId}
                      style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '18px 22px', cursor: 'pointer', transition: 'all 0.15s', position: 'relative' }}
                      onClick={() => navigate(`/study/${id?.nctId}`)}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--ink)'; e.currentTarget.style.boxShadow = '6px 6px 0 rgba(10,10,10,0.06)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--line-card)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      {study.hasResults && <span style={{ position: 'absolute', top: 14, right: 14, background: 'var(--ink)', color: 'var(--paper)', fontSize: 9, padding: '2px 7px', borderRadius: 0, letterSpacing: '1.5px', fontFamily: 'var(--font-mono)' }}>Results</span>}
                      <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: 'var(--ink)', marginBottom: 10, paddingRight: 64 }}>{id?.briefTitle}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--muted-2)' }}>{id?.nctId}</span>
                        {st?.overallStatus && <StatusTag status={st.overallStatus} />}
                        {design?.phases?.map(ph => <span key={ph} style={{ fontFamily: 'var(--font-mono)', fontSize: 10, padding: '2px 7px', borderRadius: 0, border: '1px solid var(--line-card)', color: 'var(--muted)' }}>{ph.replace('PHASE', 'Phase ')}</span>)}
                        {sp?.name && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, padding: '2px 7px', borderRadius: 0, border: '1px solid var(--line-card)', color: 'var(--muted)' }}>{sp.name}</span>}
                        {conditions.slice(0, 2).map((c, i) => <span key={i} style={{ fontFamily: 'var(--font-mono)', fontSize: 10, padding: '2px 7px', borderRadius: 0, border: '1px solid var(--line-card)', color: 'var(--muted)' }}>{c}</span>)}
                      </div>
                      {desc?.briefSummary && <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{desc.briefSummary}</div>}
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 32, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button style={{ width: 34, height: 34, border: '1px solid var(--line)', background: 'var(--card)', borderRadius: 0, fontSize: 13, cursor: 'pointer' }} onClick={() => handlePageChange(1)} disabled={currentPage === 1}>«</button>
                  <button style={{ width: 34, height: 34, border: '1px solid var(--line)', background: 'var(--card)', borderRadius: 0, fontSize: 13, cursor: 'pointer' }} onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>‹</button>
                  {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                    let pg;
                    if (totalPages <= 7) pg = i + 1;
                    else if (currentPage <= 4) pg = i + 1;
                    else if (currentPage >= totalPages - 3) pg = totalPages - 6 + i;
                    else pg = currentPage - 3 + i;
                    const isActive = currentPage === pg;
                    return (
                      <button key={pg}
                        style={{ width: 34, height: 34, border: `1px solid ${isActive ? 'var(--ink)' : 'var(--line)'}`, background: isActive ? 'var(--ink)' : 'var(--card)', color: isActive ? 'var(--paper)' : 'var(--ink)', borderRadius: 0, fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-mono)' }}
                        onClick={() => handlePageChange(pg)}
                      >{pg}</button>
                    );
                  })}
                  <button style={{ width: 34, height: 34, border: '1px solid var(--line)', background: 'var(--card)', borderRadius: 0, fontSize: 13, cursor: 'pointer' }} onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
                  <button style={{ width: 34, height: 34, border: '1px solid var(--line)', background: 'var(--card)', borderRadius: 0, fontSize: 13, cursor: 'pointer' }} onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>»</button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}